import React, { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	Typography,
	Avatar,
	Button,
	Card,
	CardContent,
	Divider,
	Box,
	CircularProgress,
	useMediaQuery,
	useTheme,
} from "@material-ui/core";
import { Close, Warning, CheckCircle, Cancel, Event, Person } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { formatDateByBusinessLanguage } from "~/utils/dateUtils";
import { getInitials, getAvatarFontSize } from "~/utils/business";
import MuiStylesAppointmentCancelDialog from "./MuiStylesAppointmentCancelDialog";
import bookingTransactionService from "~/services/bookingTransactionService";
import FeedbackModalDetailsSchedules from "./FeedbackModalDetailsSchedules";
import ModalVerifyAccess from "~/components/common/ModalVerifyAccess";
import { mixpanelTrackEvent } from "~/utils/mixpanel";
import Utils from "~/helpers/Utils";

const AppointmentCancelDialog = ({
	open,
	onClose,
	business,
	professional,
	avatarProfessional,
	serviceName,
	startTime,
	date,
	dateEnd,
	pricingType,
	currency,
	loading = false,
	showSuccess = false,
	paymentCancellation,
	bookingId,
	setOpen,
	setOpenErrorCancel,
	setMessageError,
	history,
	onCancelSuccess,
	isOwnerUser,
	currentUser,
	origemLink = "outro",
}) => {
	const classes = MuiStylesAppointmentCancelDialog();
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [openCancel, setOpenCancel] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [showVerifyAccessModal, setShowVerifyAccessModal] = useState(false);

	if (!open) return null;

	const mockData = {
		business: {
			name: business?.name,
			address: business?.address?.street,
			initials: getInitials(business?.name),
		},
		appointment: {
			date: formatDateByBusinessLanguage(startTime || date, business?.language),
			timeRange: `${moment(date).format("HH:mm")} - ${moment(dateEnd).format("HH:mm")}`,
		},
		professional: {
			name: professional,
			avatar: avatarProfessional,
		},
		service: {
			name: serviceName,
			priceType: pricingType?.name || "Consulta",
		},
		payment: {
			totalServices: Utils.formatMoney((paymentCancellation?.total_values_for_services / 100), currency),
			discount: Utils.formatMoney((paymentCancellation?.discount / 100), currency),
			advancePayment: Utils.formatMoney((paymentCancellation?.advance_payment / 100), currency),
			cancellationFee: Utils.formatMoney((paymentCancellation?.cancellation_fee / 100), currency),
		},
	};

	const cancelBookingTransaction = async (reason) => {
		setIsLoading(true);
		try {
			const bookingCancel = await bookingTransactionService.putBookingStatus(
				bookingId,
				reason || "Cancelamento solicitado pelo cliente",
				"cancelled",
				"client"
			);
			if (bookingCancel) {
				const serviceValue =
					paymentCancellation?.total_values_for_services != null
						? paymentCancellation.total_values_for_services / 100
						: null;
					mixpanelTrackEvent(
					"Cancelar_Agendamento",
					{
						Motivo: reason || "Cancelamento solicitado pelo cliente",
						Valor_Servico: serviceValue,
						Quem_Cancelou: isOwnerUser ? "Estabelecimento" : "Cliente",
					},
					currentUser,
					business
				);
				setOpen(false);
				if (onCancelSuccess) {
					onCancelSuccess();
				} else {
					setOpenCancel(true);
				}
			}
		} catch (error) {
			if (error && error.response && error.response.status === 403) {
				if (error?.response?.data?.code === "booking_owner_mismatch") {
					mixpanelTrackEvent(
						"Acesso_Negado_Reserva",
						{
							company_id: business?.id,
							origem_link: origemLink,
							sessao_e_do_estabelecimento: isOwnerUser,
							tem_sessao_ativa: !!currentUser?.id,
							tela: "Dialog_Cancelamento_Reserva",
						},
						currentUser,
						business
					);
				}
				setShowVerifyAccessModal(true);
			} else {
				setMessageError(error?.message ?? t("An error occurred while canceling"));
				setOpenErrorCancel(true);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={onClose}
				fullScreen={isMobile}
				PaperProps={{
					className: classes.dialogPaper,
				}}>
				<DialogContent className={classes.dialogContent}>
					{showSuccess ? (
						<Box className={classes.successContainer}>
							<div className={classes.successIcon}>
								<CheckCircle className={classes.successIconSvg} />
							</div>
							<Typography className={classes.successTitle}>{t("Agendamento cancelado com sucesso!")}</Typography>
							<Typography className={classes.successMessage}>
								{t("Você receberá um e-mail de confirmação em breve.")}
							</Typography>
						</Box>
					) : (
						<>
							<Card className={classes.detailsCard}>
								<CardContent>
									<Box className={classes.businessSection}>
										<Avatar 
											className={classes.businessAvatar}
											src={business?.thumbnailUrl ? business.thumbnailUrl : ""}
										>
											<Typography style={{ fontSize: getAvatarFontSize(business?.name) }}>
												{getInitials(business?.name)}
											</Typography>
										</Avatar>
										<div className={classes.businessInfo}>
											<Typography className={classes.businessName}>{mockData?.business?.name}</Typography>
											<Typography className={classes.businessAddress}>{mockData?.business?.address}</Typography>
										</div>
									</Box>

									<Divider style={{ margin: "8px 0" }} />

									<Box className={classes.appointmentInfo}>
										<Event className={classes.detailIcon} />
										<div>
											<Typography className={classes.appointmentDate}>{mockData?.appointment?.date}</Typography>
											<Typography className={classes.appointmentTime}>{mockData?.appointment?.timeRange}</Typography>
										</div>
									</Box>

									<Box className={classes.professionalSection}>
										<Person className={classes.detailIcon} />
										<div className={classes.professionalInfo}>
											<Typography className={classes.professionalName}>
												<span>{t("with")} {mockData.professional.name}</span>
											</Typography>
											<Typography className={classes.serviceInfo}>{mockData?.service?.name}</Typography>
											<Typography className={classes.priceTag}>{mockData?.service?.priceType}</Typography>
										</div>
									</Box>

									<Divider style={{ margin: "8px 0" }} />

									<Box className={classes.paymentSection}>
										<Typography className={classes.paymentTitle}>{t("Payment details")}</Typography>

										<Box className={classes.paymentRow}>
											<Typography className={classes.paymentLabel}>{t("Total services")}</Typography>
											<Typography className={classes.paymentValue}>{mockData?.payment?.totalServices}</Typography>
										</Box>

										<Box className={classes.paymentRow}>
											<Typography className={classes.paymentLabel}>{t("Discount")}</Typography>
											<Typography className={classes.paymentValue}>{mockData?.payment?.discount}</Typography>
										</Box>

										<Box className={classes.paymentRow}>
											<Typography className={classes.paymentLabel}>{t("Payment advance")}</Typography>
											<Typography className={classes.paymentValue}>{mockData?.payment?.advancePayment}</Typography>
										</Box>

										<Box className={classes.paymentRow}>
											<Typography className={classes.paymentLabel}>{t("cancellation fee")}</Typography>
											<Typography className={classes.paymentValue}>{mockData?.payment?.cancellationFee}</Typography>
										</Box>
									</Box>
									<Divider style={{ margin: "8px 0" }} />
									<Box className={classes.warningWrapper}>
										<Box className={classes.warningSection}>
											<div className={classes.warningIcon}>
												<Warning className={classes.warningIconSvg} />
											</div>
											<div>
												<Typography className={classes.warningTitle}>
													{t("Are you sure you want to cancel this appointment")}
												</Typography>
												<Typography className={classes.warningMessage}>
													{t("This action cannot be undone")}
												</Typography>
											</div>
										</Box>
									</Box>
								</CardContent>
							</Card>

							<Box className={classes.actionButtons}>
								<Button className={classes.backButton} onClick={onClose} disabled={loading || isLoading}>
									{t("Back")}
								</Button>
								<Button
									className={classes.confirmCancelButton}
									style={{ backgroundColor: "#8C1837" }}
									onClick={() => cancelBookingTransaction()}
									disabled={loading || isLoading}
									startIcon={loading || isLoading ? <CircularProgress size={16} color="inherit" /> : <Cancel />}>
									{loading || isLoading ? t("Cancelling") : t("Cancelar")}
								</Button>
							</Box>
						</>
					)}
				</DialogContent>
			</Dialog>

			<FeedbackModalDetailsSchedules
				open={openCancel}
				onClose={() => setOpenCancel(false)}
				business={business}
				history={history}
			/>
			<ModalVerifyAccess
				open={showVerifyAccessModal}
				onClose={() => setShowVerifyAccessModal(false)}
			/>
		</>
	);
};

export default AppointmentCancelDialog;
